import React, { FC, useState } from 'react';

import { Collapse, TextField, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';

import ThumbUpIcon from '~/assets/icons/ThumbUpIcon';
import CustomButton from '~/components/UI/CustomButton/CustomButton';
import { BodyText, H3 } from '~/components/UI/Texts';
import UnstyledButton from '~/components/UI/UnstyledButton';
import useIsSsku from '~/hooks/tenant/useIsSsku';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { VIDEO_PAGE_EVENTS } from '~/pages/SingleVideoPage/const';
import { IUseVideoReview } from '~/pages/SingleVideoPage/hooks/useVideoReview';
import { CustomThumbDownIcon, CustomThumbUpIcon } from '~/pages/SingleVideoPage/styles';
import {
  BIG_MARGIN_PX,
  MIDDLE_MARGIN_PX,
  MIDDLE_RADIUS_PX,
  SMALL_MARGIN_PX,
  SUBMIDDLE_MARGIN_PX,
} from '~/theme';
import sendAnalyticEvent from '~/utils/sendAnalyticEvent';

interface CommentFormProps {
  title?: string | null;
  leaveReview: IUseVideoReview['leaveReview'];
  likedVideo?: boolean;
  likedOutsideCommentSection: boolean;
  leaveReviewWithComment: (comment: string) => void;
  loading: boolean;
}

const CommentForm: FC<CommentFormProps> = ({
  title,
  likedVideo,
  leaveReview,
  likedOutsideCommentSection,
  leaveReviewWithComment,
  loading,
}) => {
  const [comment, setComment] = useState('');
  const isSsku = useIsSsku();
  const { t } = useTenantTranslation();
  const theme = useTheme();

  const handleChangeInput = (e) => {
    setComment(e.target.value);
  };

  const handleSubmit = () => {
    sendAnalyticEvent(VIDEO_PAGE_EVENTS.comment);
    leaveReviewWithComment(comment.trim());
  };

  return (
    <Wrapper data-testid="commentForm" id="commentForm">
      {!likedOutsideCommentSection && <BodyText>{t('page.video.letUsKnow')}</BodyText>}
      {likedOutsideCommentSection && likedVideo === true && (
        <Row>
          <CustomThumbUpIcon filled={true} color={theme.palette.common.primary} />
          <BodyTextWithMargin>{t('page.video.highlySatisfiedDesc')}</BodyTextWithMargin>
        </Row>
      )}
      {likedOutsideCommentSection && likedVideo === false && (
        <Row>
          <CustomThumbDownIcon filled={true} color={theme.palette.common.primary} />
          <BodyTextWithMargin>{t('page.video.dissatisfiedDesc')}</BodyTextWithMargin>
        </Row>
      )}
      <H3>{title}</H3>
      {!likedOutsideCommentSection && (
        <SelectWrapper>
          <UnstyledButton
            data-testid="commentFormThumbUp"
            onClick={() => {
              sendAnalyticEvent(VIDEO_PAGE_EVENTS.thumbUp);
              leaveReview({ liked: true });
            }}
          >
            <BigThumbUpIcon
              filled={likedVideo}
              color={theme.palette.common.primary}
              animated={true}
            />
          </UnstyledButton>
          <UnstyledButton
            data-testid="commentFormThumbDown"
            onClick={() => {
              sendAnalyticEvent(VIDEO_PAGE_EVENTS.thumbDown);
              leaveReview({ liked: false });
            }}
          >
            <BigThumbDownIcon filled={likedVideo === false} color={theme.palette.common.primary} />
          </UnstyledButton>
        </SelectWrapper>
      )}
      <StyledCollapse in={likedVideo !== undefined && !isSsku}>
        <StyledTextField
          label={t('page.video.yourComment')}
          variant="outlined"
          multiline
          rows={3}
          value={comment}
          onChange={handleChangeInput}
          inputProps={{
            'data-testid': 'commentMessageField',
          }}
        />
        <CustomButton data-testid="commentFormSubmitBtn" disabled={loading} onClick={handleSubmit}>
          {t('button.leaveComment')}
        </CustomButton>
      </StyledCollapse>
    </Wrapper>
  );
};

export default CommentForm;

const Wrapper = styled('div')`
  padding: ${MIDDLE_MARGIN_PX} ${SMALL_MARGIN_PX};
  border-radius: ${MIDDLE_RADIUS_PX};
  background-color: ${({ theme }) => theme.palette.common.card.overlay.main};
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${MIDDLE_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    padding: ${BIG_MARGIN_PX} ${SUBMIDDLE_MARGIN_PX} ${MIDDLE_MARGIN_PX} ${SUBMIDDLE_MARGIN_PX};
  }
`;
const Row = styled('div')`
  display: flex;
  align-items: center;
`;
const BodyTextWithMargin = styled(BodyText)`
  margin-left: 4px;
`;
const SelectWrapper = styled('div')`
  display: flex;
  align-items: center;
  margin-top: ${SMALL_MARGIN_PX};
  & > *:first-child {
    margin-right: ${SMALL_MARGIN_PX};
  }
`;
const BigThumbUpIcon = styled(ThumbUpIcon)`
  width: 36px;
  height: 36px;
`;
const BigThumbDownIcon = styled(CustomThumbDownIcon)`
  width: 36px;
  height: 36px;
`;
const StyledCollapse = styled(Collapse)`
  padding-top: ${SUBMIDDLE_MARGIN_PX};
  max-width: 780px;
  width: 100%;
  &.MuiCollapse-hidden {
    padding-top: 0;
  }
  & .MuiCollapse-wrapperInner {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;
const StyledTextField = styled(TextField)`
  margin-bottom: ${SMALL_MARGIN_PX};
  width: 100%;
  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-bottom: ${MIDDLE_MARGIN_PX};
  }
`;
